import type React from "react";
import { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { Attachment } from "../types";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import { cn } from "../utils/cn";

interface ImageSwiperProps {
  isLandscape: boolean;
  carouselItems: Attachment[];
  setSelectedImage: React.Dispatch<React.SetStateAction<string | null>>;
}

export default function ImageSwiper({
  isLandscape,
  carouselItems,
  setSelectedImage,
}: ImageSwiperProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSlideChange = (swiper: SwiperType) => {
    setCurrentIndex(swiper.activeIndex);
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  return (
    <div className={cn("w-full h-full ", !isLandscape && carouselItems.length > 1 ? "pb-5" : !isLandscape ? 'pb-12' : '')}>
      <div className={cn("w-full", isLandscape && "h-full py-[1.88vw] rounded-[4.7vw] overflow-hidden")}>
        <Swiper
          modules={[EffectCoverflow, Pagination]}
          spaceBetween={isLandscape ? 24 : 16}
          slidesPerView={"auto"}
          centeredSlides={true}
          onSlideChange={handleSlideChange}
          className="w-full h-full"
          effect="coverflow"
          grabCursor={true}
          initialSlide={0}
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 0,
            modifier: 2.5,
            slideShadows: false,
          }}
        >
          {carouselItems.map((item, index) => (
            <SwiperSlide
              key={index}
              className={cn(isLandscape ? "w-fit" : "w-[65%] max-w-[300px]")}
            >
              <div
                className={cn(
                  "aspect-square overflow-hidden rounded-[32px] bg-white/25",
                  isLandscape ? "h-full relative" : "w-full max-h-[300px] "
                )}
              >
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => handleImageClick(item.url)}
                />
                <div
                  className={cn(
                    "absolute inset-0 bg-[rgba(229,229,229,0.6)] transition-opacity duration-300",
                    isLandscape && currentIndex !== index ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                  )}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Page Indicator */}
      {carouselItems.length > 1 && (
        <div className={cn('w-fit mx-auto', isLandscape ? 'pt-2 lg:pt-[2vw] text-[1.88vw]' : 'pt-2 text-base')}>
          <span className="text-[#333333]">
            {currentIndex + 1}/{carouselItems.length}
          </span>
        </div>
      )}
    </div>
  );
}
